import React from 'react';
import { awardShowcaseItems, AwardShowcaseItem } from '../data/awards';

export interface AwardsShowcaseModuleProps {
    className?: string;
    title1?: string;
    title2?: string;
    description?: string;
    badgeText?: string | null;
    awards?: AwardShowcaseItem[];
    children?: React.ReactNode;
}

const DEFAULT_TITLE_1 = 'Award-winning headphones.';
const DEFAULT_TITLE_2 = 'Recognised around the world.';
const DEFAULT_DESCRIPTION =
    'From sustainable design to circular manufacturing, Kibu has been recognised by leading design, technology and future-focused awards.';

const CARD_CLASS =
    'absolute top-1/2 aspect-[4/5] w-[42%] max-w-[18rem] -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-[0_24px_55px_rgba(0,0,0,0.26)] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none sm:w-[36%] md:w-[40%]';

const getAwardCardClassName = (index: number, activeIndex: number, total: number): string => {
    const offset = (index - activeIndex + total) % total;

    if (offset === 0) {
        return `${CARD_CLASS} left-1/2 z-[4] -translate-x-1/2 scale-100 opacity-100 rotate-0`;
    }

    if (offset === 1) {
        return `${CARD_CLASS} left-[73%] z-[3] -translate-x-1/2 scale-[0.78] opacity-60 rotate-[3deg]`;
    }

    if (offset === total - 1) {
        return `${CARD_CLASS} left-[27%] z-[2] -translate-x-1/2 scale-[0.78] opacity-45 rotate-[-3deg]`;
    }

    return `${CARD_CLASS} left-[84%] z-[1] -translate-x-1/2 scale-[0.64] opacity-0 rotate-[6deg]`;
};

const AwardsCoverFlow: React.FC<{ awards: AwardShowcaseItem[] }> = ({ awards }) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const total = awards.length;

    React.useEffect(() => {
        if (total <= 1) return undefined;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) return undefined;

        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % total);
        }, 1000);

        return () => window.clearInterval(timer);
    }, [total]);

    return (
        <div
            className="relative h-[19rem] w-full overflow-visible md:h-[28rem]"
            aria-label="Kibu awards carousel"
        >
            {awards.map((award, index) => {
                const isActive = index === activeIndex;

                return (
                    <a
                        key={award.id}
                        href={award.href}
                        className={getAwardCardClassName(index, activeIndex, total)}
                        aria-label={`${award.name}: ${award.title} ${award.year}`}
                        tabIndex={isActive ? 0 : -1}
                    >
                        <img
                            src={award.image}
                            alt=""
                            className="h-full w-full object-contain"
                            aria-hidden
                        />
                    </a>
                );
            })}
        </div>
    );
};

const AwardsShowcaseModule: React.FC<AwardsShowcaseModuleProps> = ({
    className = '',
    title1 = DEFAULT_TITLE_1,
    title2 = DEFAULT_TITLE_2,
    description = DEFAULT_DESCRIPTION,
    badgeText = 'Winner',
    awards = awardShowcaseItems,
    children,
}) => {
    const mergedSectionClass = `w-full ${className}`.trim();

    return (
        <section className={mergedSectionClass}>
            <div className="relative overflow-hidden rounded-2xl bg-specialYellow text-black">
                {badgeText ? (
                    <div className="absolute z-[7] top-4 left-4 md:left-6 rounded-lg bg-black px-4 py-2 text-sm md:text-base font-headline leading-none text-specialYellow shadow-[0_12px_28px_rgba(0,0,0,0.16)] rotate-[-5deg]">
                        {badgeText}
                    </div>
                ) : null}

                <div className="grid min-h-[34rem] grid-cols-1 gap-4 px-8 pt-14 pb-0 md:min-h-[28rem] md:grid-cols-[minmax(0,0.92fr)_minmax(26rem,1.08fr)] md:items-center md:gap-8 md:p-14 md:pt-14">
                    <div className="relative z-[5] flex flex-col gap-5 md:pr-4">
                        <div className="md:mb-2">
                            <h3 className="text-3xl md:text-5xl font-black leading-tight">{title1}</h3>
                            {title2 ? (
                                <h3 className="text-3xl md:text-5xl font-black leading-tight">{title2}</h3>
                            ) : null}
                        </div>

                        <p className="text-lg md:text-xl text-black/75 leading-relaxed">
                            {description}
                        </p>
                        {children != null ? (
                            <div className="self-start w-full md:w-auto max-w-full">
                                {children}
                            </div>
                        ) : null}
                    </div>

                    <div className="relative z-[4] -mx-4 -mt-6 md:mx-0 md:mt-0">
                        <AwardsCoverFlow awards={awards} />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AwardsShowcaseModule;
